import {
  Component,
  Inject,
  Input,
  Optional,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PhxFile, PHX_FILE_LOADING_STATUS, PhxFileErrorCode, PhxFileEventPayload } from '../phx-file-upload.types';
import { Bullet16, Cross, Document, Image } from '../../icon/icon-map';
import { DomSanitizer } from '@angular/platform-browser';
import { KeyboardKeys } from '../../../helpers/KeyboardKeys.enum';
import { PhxTranslatePipe } from '../../../translation/phx-translation.pipe';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../../locale/phx-locale.token';

@Component({
  selector: 'phx-file-item',
  templateUrl: './phx-file-item.component.html',
  styleUrls: ['./phx-file-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-file-item',
    '[class.phx-file-no-details]': '!phxShowDetails',
    '[class.phx-file-edit-mode]': 'phxIsEditMode',
    '[class.phx-file-horizontal]': 'phxShowDetails && phxIsEditMode',
    '[class.phx-file-uploading]': 'status === PHX_FILE_LOADING_STATUS.PROGRESS',
    '[class.phx-file-error]': 'status === PHX_FILE_LOADING_STATUS.ERROR',
    '[class.phx-file-done]': 'status === PHX_FILE_LOADING_STATUS.DONE',
    '[class.phx-file-clickable]': 'status === PHX_FILE_LOADING_STATUS.DONE',
    '[class.phx-file-disabled]': 'isDisabled',
    '[class.phx-file-tile-mode]': 'phxTileMode',
    '[style.width.px]': 'hostWidthPx',
    '[style.height.px]': 'hostHeightPx',
    '[attr.tabindex]': 'isFileItemFocusable ? 0 : -1',
    '(click)': 'onPreview()',
    '(keydown)': 'onKeydown($event)'
  },
})
export class PhxFileComponent implements OnInit, OnDestroy {
  @Input() public phxFile!: PhxFile;
  @Input() public phxIndex: number;

  @Input() public phxIsEditMode = false;
  @Input() public phxShowDetails = true;
  @Input() public phxObfuscate = false;
  @Input() public phxWidth = 56;
  @Input() public phxHeight = 56;

  /** Renders the item as a square image tile (used by the TILES layout): fills its grid cell,
   *  shows a size badge + always-visible delete button, and a centered spinner while loading. */
  @Input() public phxTileMode = false;

  @Output() public phxRemoveFile = new EventEmitter<{ file: PhxFile, index: number }>();
  @Output() public phxRetryUpload = new EventEmitter<PhxFileEventPayload>();
  @Output() public phxPreview = new EventEmitter<{ file: PhxFile; index: number }>();

  public readonly PHX_FILE_LOADING_STATUS = PHX_FILE_LOADING_STATUS;
  public readonly crossIcon = Cross;
  public readonly documentIcon = Document;
  public readonly imageIcon = Image;
  public readonly bulletIcon = Bullet16;
  public status: PHX_FILE_LOADING_STATUS = PHX_FILE_LOADING_STATUS.START;
  public progress: number = 0;

  /**
   * True only when the image/video element actually failed to load (broken URL / fetch failed),
   * as opposed to a consumer-reported validation error. Lets tile mode keep showing a valid image
   * under a red border while still falling back to an icon for genuinely unloadable media.
   */
  public imageLoadFailed = false;

  /** True when the tile-mode PDF preview failed to render; falls back to the document icon. */
  public pdfLoadFailed = false;

  private destroy$ = new Subject<void>();

  public get showRetry(): boolean {
    return this.status === PHX_FILE_LOADING_STATUS.ERROR && this.phxFile?.retryable === true;
  }

  public get isDisabled(): boolean {
    return this.phxFile?.disabled === true;
  }

  public get isFileItemFocusable(): boolean {
    return !this.isDisabled && this.status !== PHX_FILE_LOADING_STATUS.ERROR;
  }

  public get hostWidthPx(): number | null {
    return this.shouldSetHostSize ? this.phxWidth : null;
  }

  public get hostHeightPx(): number | null {
    return this.shouldSetHostSize ? this.phxHeight : null;
  }

  private get shouldSetHostSize(): boolean {
    return !this.phxTileMode && (!this.phxShowDetails || !this.phxIsEditMode);
  }

  constructor(
    private _cdr: ChangeDetectorRef,
    private _sanitizer: DomSanitizer,
    private _phxTranslate: PhxTranslatePipe,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private _phxLocale: PhxLocaleProvider | null,
  ) { }
  public ngOnInit(): void {
    this.phxFile?.status$?.pipe(
      takeUntil(this.destroy$)
    ).subscribe(status => {
      if (status !== PHX_FILE_LOADING_STATUS.ERROR) {
        this.imageLoadFailed = false;
        this.pdfLoadFailed = false;
      }
      this.status = status;
      this._cdr.markForCheck();
    });

    this.phxFile?.progress$?.pipe(
      takeUntil(this.destroy$)
    ).subscribe(progress => {
      this.progress = progress;
      this._cdr.markForCheck();
    });

  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onRemove(event: Event): void {
    event.stopPropagation();
    if (this.isDisabled) return;
    this.phxRemoveFile.emit({ file: this.phxFile, index: this.phxIndex });
  }

  public onRetry(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.phxRetryUpload.emit({ file: this.phxFile, index: this.phxIndex });
  }

  public onPreview(): void {
    if (!this.isDisabled) {
      this.phxPreview.emit({ file: this.phxFile, index: this.phxIndex });
    }
  }

  public onPdfLoadError(): void {
    this.pdfLoadFailed = true;
    this._cdr.markForCheck();
  }

  public onContextMenu(event: MouseEvent): void {
    if (this.phxObfuscate) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  public onFileLoadError(): void {
    this.status = PHX_FILE_LOADING_STATUS.ERROR;
    this.imageLoadFailed = true;

    const isLocalBlob = this.phxFile.url?.startsWith('blob:');
    const errorMessage = isLocalBlob
      ? this._phxTranslate.transform('File is corrupted')
      : this._phxTranslate.transform('Fetch Failed');
    const errorCode = isLocalBlob
      ? PhxFileErrorCode.CORRUPT_FILE
      : PhxFileErrorCode.FETCH_FAILED;

    this.phxFile.error = errorMessage;
    this.phxFile.errorMessage = errorMessage;
    this.phxFile.errorCode = errorCode;
    this.phxFile.retryable = !isLocalBlob;

    this.phxFile.status$?.next(PHX_FILE_LOADING_STATUS.ERROR);
    this._cdr.markForCheck();
  }

  public formatFileSize(bytes?: number): string {
    if (!bytes) return `0 ${this._phxTranslate.transform('B')}`;
    const sizes = [
      this._phxTranslate.transform('B'),
      this._phxTranslate.transform('KB'),
      this._phxTranslate.transform('MB'),
      this._phxTranslate.transform('GB')
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    const formatted = this._phxLocale ? this._phxLocale.formatNumber(value, 1) : value.toFixed(1);
    return `${formatted} ${sizes[i]}`;
  }

  public get uploadedSize(): string {
    if (!this.phxFile.size || !this.progress) return '0 B';
    const uploadedBytes = Math.floor((this.phxFile.size * this.progress) / 100);
    return this.formatFileSize(uploadedBytes);
  }

  public get safeUrl() {
    return this._sanitizer.bypassSecurityTrustUrl(this.phxFile.url);
  }

  public get isImage(): boolean {
    return this.phxFile.type ? ['png', 'jpg', 'jpeg'].includes(this.phxFile.type) : false;
  }

  public get isVideo(): boolean {
    return this.phxFile.type ? ['mp4', 'mov', 'webm'].includes(this.phxFile.type) : false;
  }

  public get isPDF(): boolean {
    return this.phxFile.type === 'pdf';
  }

  public get isGeoJson(): boolean {
    return this.phxFile.type ? ['geojson', 'json'].includes(this.phxFile.type) : false;
  }

  public get isUnknownFileType(): boolean {
    return !this.isImage && !this.isVideo && !this.isPDF && !this.isGeoJson;
  }

  public onKeydown(event: KeyboardEvent): void {
    if ((event.code === KeyboardKeys.SPACE || event.code === KeyboardKeys.ENTER) && event.target === event.currentTarget) {
      event.preventDefault();
      this.onPreview();
    }
  }
}
